import { useEffect, useRef } from 'react';
import NicknameBar from '../components/NicknameBar';

interface LotteryTexts {
  winnerTitle?: string;
  winnerSubtitle?: string;
}

interface LotteryWinnerScreenProps {
  lotteryId: string;
  isWinner: boolean;
  texts?: LotteryTexts;
}

// Componente Coriandolo
interface ConfettiPiece {
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  speedY: number;
  speedX: number;
  speedRotation: number;
}

function LotteryWinnerScreen({ isWinner, texts }: LotteryWinnerScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<ConfettiPiece[]>([]);
  const animationRef = useRef<number>(0);

  // Genera coriandoli
  useEffect(() => {
    if (!isWinner) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resize canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Colori coriandoli
    const colors = [
      '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
      '#ff8800', '#ff0088', '#88ff00', '#0088ff', '#8800ff', '#00ff88',
    ];

    // Genera coriandoli iniziali
    const generateConfetti = () => {
      const pieces: ConfettiPiece[] = [];
      for (let i = 0; i < 150; i++) {
        pieces.push({
          x: Math.random() * canvas.width,
          y: -20 - Math.random() * canvas.height,
          rotation: Math.random() * 360,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 8 + Math.random() * 12,
          speedY: 2 + Math.random() * 4,
          speedX: -2 + Math.random() * 4,
          speedRotation: -5 + Math.random() * 10,
        });
      }
      return pieces;
    };

    confettiRef.current = generateConfetti();

    // Animazione
    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confettiRef.current.forEach((piece) => {
        // Aggiorna posizione
        piece.y += piece.speedY;
        piece.x += piece.speedX;
        piece.rotation += piece.speedRotation;

        // Reset se esce dallo schermo
        if (piece.y > canvas.height + 20) {
          piece.y = -20;
          piece.x = Math.random() * canvas.width;
        }

        // Disegna coriandolo
        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate((piece.rotation * Math.PI) / 180);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2);
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, [isWinner]);

  if (!isWinner) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 pt-10">
        <NicknameBar />
        <div className="text-center">
          <div className="text-6xl mb-4">😌</div>
          <h2 className="text-3xl font-heading font-bold text-ink mb-2">Non sei stato scelto</h2>
          <p className="text-lg text-ink-light">Questa volta tocca a qualcun altro!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen p-4 pt-10 overflow-hidden">
      <NicknameBar />

      {/* Canvas coriandoli */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 10 }}
      />

      {/* Contenuto centrale */}
      <div className="relative z-20 text-center">
        <div className="text-8xl mb-6 animate-bounce">🎉</div>
        <h1 className="text-4xl font-heading font-bold text-team-yellow mb-4 animate-pulse">
          {texts?.winnerTitle || 'SEI STATO SCELTO!'}
        </h1>
        <p className="text-xl text-ink">
          {texts?.winnerSubtitle || 'Preparati ad essere coinvolto!'}
        </p>
      </div>
    </div>
  );
}

export default LotteryWinnerScreen;
